import {
  assignmentOfBenefitsOptions,
  billingStateOptions,
  cappedRentalEquipmentCategories,
  capResetsOnOptions,
  claimFrequencyTypeOptions,
  contractTypes,
  coordinationOfBenefitsOptions,
  correctedClaimDeadlineOptions,
  documentationRequiredOptions,
  feeBasisOptions,
  feeScheduleModifierOptions,
  ownershipTransferModifierOptions,
  paSubmissionMethodOptions,
  planNetworkOptions,
  planTypes,
  renderingProviderRequiredOptions,
  rentalCapPeriodOptions,
  standardTurnaroundTimeOptions,
  submissionMethodOptions,
  timelyFilingLimitOptions,
  transferTriggerOptions,
} from './payorData'
import type { MasterKey, MasterOption } from '../services/mastersService'

export type MasterCategoryId =
  | 'core'
  | 'payor-contract'
  | 'plans-policies'
  | 'claims-rules'
  | 'fee-rentals'
  | 'ownership-transfer'
  | 'prior-auth'

export type MasterModuleDef = {
  key: MasterKey
  name: string
  description: string
  placeholder: string
  category: MasterCategoryId
}

export const MASTER_CATEGORIES: Array<{ id: MasterCategoryId; label: string }> = [
  { id: 'core', label: 'Core' },
  { id: 'payor-contract', label: 'Payor & Contract' },
  { id: 'plans-policies', label: 'Plans & Policies' },
  { id: 'claims-rules', label: 'Claims Rules' },
  { id: 'fee-rentals', label: 'Fee Schedule & Rentals' },
  { id: 'ownership-transfer', label: 'Ownership Transfer' },
  { id: 'prior-auth', label: 'Prior Authorization' },
]

function opts(items: Array<{ value: string; label: string }>): MasterOption[] {
  return items
    .filter((item) => item.value.trim())
    .map((item, index) => ({
      value: item.value,
      label: item.label,
      isActive: true,
      sortOrder: index + 1,
    }))
}

function labels(items: string[]): MasterOption[] {
  return items.map((label, index) => ({
    value: label,
    label,
    isActive: true,
    sortOrder: index + 1,
  }))
}

export const MASTER_MODULES: MasterModuleDef[] = [
  {
    key: 'payors',
    name: 'Payors',
    description: 'Insurance payor names used across configuration and intake.',
    placeholder: 'Select payor',
    category: 'core',
  },
  {
    key: 'plans',
    name: 'Plans',
    description: 'Plan names mapped under payors.',
    placeholder: 'Select plan',
    category: 'core',
  },
  {
    key: 'product-services',
    name: 'Product / Services',
    description: 'Procedure and product service options for CPT/HCPC and intake.',
    placeholder: 'Select product / service',
    category: 'core',
  },
  {
    key: 'cpt-categories',
    name: 'CPT Categories',
    description: 'Category options such as CPT and HCPC.',
    placeholder: 'Select category',
    category: 'core',
  },
  {
    key: 'authorization-statuses',
    name: 'Authorization Statuses',
    description: 'Whether prior authorization is required for a code or service.',
    placeholder: 'Select authorization status',
    category: 'core',
  },
  {
    key: 'contract-types',
    name: 'Contract Type',
    description: 'Payor contract arrangement (HMO, PPO, Medicare Advantage, etc.).',
    placeholder: 'Select contract type',
    category: 'payor-contract',
  },
  {
    key: 'billing-states',
    name: 'Billing State',
    description: 'States where claims are billed for this payor contract.',
    placeholder: 'Select state',
    category: 'payor-contract',
  },
  {
    key: 'submission-methods',
    name: 'Submission Method',
    description: 'How claims are submitted to the payor.',
    placeholder: 'Select method',
    category: 'payor-contract',
  },
  {
    key: 'plan-types',
    name: 'Plan Type',
    description: 'Plan benefit design (PPO, HMO, EPO, etc.).',
    placeholder: 'Plan type',
    category: 'plans-policies',
  },
  {
    key: 'networks',
    name: 'Network',
    description: 'In-network, preferred, or out-of-network designation.',
    placeholder: 'Network',
    category: 'plans-policies',
  },
  {
    key: 'timely-filing-limits',
    name: 'Timely Filing Limit',
    description: 'Maximum days after date of service to submit a claim.',
    placeholder: 'Select limit',
    category: 'claims-rules',
  },
  {
    key: 'corrected-claim-deadlines',
    name: 'Corrected Claim Deadline',
    description: 'Window to resubmit or correct a denied claim.',
    placeholder: 'Select deadline',
    category: 'claims-rules',
  },
  {
    key: 'coordination-of-benefits',
    name: 'Coordination of Benefits',
    description: 'Primary, secondary, or tertiary coverage order.',
    placeholder: 'Select COB',
    category: 'claims-rules',
  },
  {
    key: 'assignment-of-benefits',
    name: 'Assignment of Benefits',
    description: 'Whether payment goes to provider or member.',
    placeholder: 'Select assignment',
    category: 'claims-rules',
  },
  {
    key: 'claim-frequency-types',
    name: 'Claim Frequency Type',
    description: 'Original, replacement, or void claim indicator.',
    placeholder: 'Select frequency type',
    category: 'claims-rules',
  },
  {
    key: 'rendering-provider-required',
    name: 'Rendering Provider Required',
    description: 'When a rendering provider must appear on the claim.',
    placeholder: 'Select requirement',
    category: 'claims-rules',
  },
  {
    key: 'modifiers',
    name: 'Modifier',
    description: 'HCPCS modifiers for fee schedule and billing (RR, NU, UE, etc.).',
    placeholder: '—',
    category: 'claims-rules',
  },
  {
    key: 'fee-basis',
    name: 'Fee Basis',
    description: 'How rental or purchase fees are calculated.',
    placeholder: 'Select fee basis',
    category: 'fee-rentals',
  },
  {
    key: 'rental-cap-periods',
    name: 'Rental Cap Period',
    description: 'Maximum rental months before ownership transfer rules apply.',
    placeholder: 'Select period',
    category: 'fee-rentals',
  },
  {
    key: 'cap-resets-on',
    name: 'Cap Resets On',
    description: 'When the rental cap counter resets.',
    placeholder: 'Select reset rule',
    category: 'fee-rentals',
  },
  {
    key: 'equipment-categories',
    name: 'Equipment Categories',
    description: 'DME equipment types subject to capped rental rules.',
    placeholder: 'Select equipment category',
    category: 'fee-rentals',
  },
  {
    key: 'transfer-triggers',
    name: 'Transfer Trigger',
    description: 'Event that initiates ownership transfer from rental to purchase.',
    placeholder: 'Select trigger',
    category: 'ownership-transfer',
  },
  {
    key: 'ownership-transfer-modifiers',
    name: 'Ownership Transfer Modifier',
    description: 'Modifiers used on ownership transfer claims (KH, KI, KJ, NU).',
    placeholder: 'Select modifier',
    category: 'ownership-transfer',
  },
  {
    key: 'documentation-required',
    name: 'Documentation Required',
    description: 'Clinical documentation needed for prior authorization.',
    placeholder: 'Select documentation',
    category: 'prior-auth',
  },
  {
    key: 'pa-submission-methods',
    name: 'PA Submission Method',
    description: 'How prior authorization requests are sent to the payor.',
    placeholder: 'Select method',
    category: 'prior-auth',
  },
  {
    key: 'standard-turnaround-times',
    name: 'Standard Turnaround',
    description: 'Expected payor response time for prior authorization.',
    placeholder: 'Select turnaround',
    category: 'prior-auth',
  },
]

/** Default seed values — aligned with payor configuration dropdowns. */
export const DEFAULT_MASTER_SEED: Record<MasterKey, MasterOption[]> = {
  payors: [
    { value: 'aetna', label: 'Aetna', isActive: true, sortOrder: 1 },
    { value: 'bcbs-tx', label: 'BlueCross BlueShield TX', isActive: true, sortOrder: 2 },
    { value: 'uhc', label: 'UnitedHealthcare', isActive: true, sortOrder: 3 },
  ],
  plans: [
    { value: 'ppo-select-plus', label: 'PPO Select Plus', isActive: true, sortOrder: 1 },
    { value: 'hmo-basic', label: 'HMO Basic', isActive: true, sortOrder: 2 },
  ],
  'product-services': [
    { value: 'oxygen-therapy', label: 'Oxygen Therapy', isActive: true, sortOrder: 1 },
    { value: 'cpap-bipap', label: 'CPAP / BiPAP', isActive: true, sortOrder: 2 },
    { value: 'mobility-support', label: 'Mobility Support', isActive: true, sortOrder: 3 },
  ],
  'cpt-categories': opts([
    { value: 'CPT', label: 'CPT' },
    { value: 'HCPC', label: 'HCPC' },
  ]),
  'authorization-statuses': [
    { value: 'required', label: 'Required', isActive: true, sortOrder: 1 },
    { value: 'not-required', label: 'Not Required', isActive: true, sortOrder: 2 },
  ],
  'contract-types': opts(contractTypes),
  'billing-states': opts(billingStateOptions),
  'submission-methods': opts(submissionMethodOptions),
  'plan-types': opts(planTypes),
  networks: opts(planNetworkOptions),
  'timely-filing-limits': opts(timelyFilingLimitOptions),
  'corrected-claim-deadlines': opts(correctedClaimDeadlineOptions),
  'coordination-of-benefits': opts(coordinationOfBenefitsOptions),
  'assignment-of-benefits': opts(assignmentOfBenefitsOptions),
  'claim-frequency-types': opts(claimFrequencyTypeOptions),
  'rendering-provider-required': opts(renderingProviderRequiredOptions),
  modifiers: opts(feeScheduleModifierOptions.filter((o) => o.value)),
  'fee-basis': opts(feeBasisOptions),
  'rental-cap-periods': opts(rentalCapPeriodOptions),
  'cap-resets-on': opts(capResetsOnOptions),
  'equipment-categories': labels(cappedRentalEquipmentCategories),
  'transfer-triggers': opts(transferTriggerOptions),
  'ownership-transfer-modifiers': opts(ownershipTransferModifierOptions),
  'documentation-required': opts(documentationRequiredOptions),
  'pa-submission-methods': opts(paSubmissionMethodOptions),
  'standard-turnaround-times': opts(standardTurnaroundTimeOptions),
}

export function getMasterModule(key: MasterKey): MasterModuleDef | undefined {
  return MASTER_MODULES.find((m) => m.key === key)
}
